import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import PlatinumIcon from "../components/PlatinumIcon";

const DEFAULT_POLL_INTERVAL = 10000;
const DEFAULT_RECENT_LIMIT = 60;
const RECENT_WINDOW_MS = 10 * 60 * 1000;
const LOWEST_VALUES_SAMPLE = 5;
const SELECTED_WEAPONS_COOKIE = "recent_selected_weapons";
const SELECTED_WEAPONS_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

function readCookie(name) {
  if (typeof document === "undefined") return "";
  const entries = String(document.cookie || "").split(";");
  const prefix = `${name}=`;
  for (const rawEntry of entries) {
    const entry = rawEntry.trim();
    if (entry.startsWith(prefix)) {
      return entry.slice(prefix.length);
    }
  }
  return "";
}

function writeCookie(name, value, maxAgeSeconds) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${value}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
}

function statusColor(status) {
  if (status === "ingame") return "bg-amber-500";
  if (status === "online") return "bg-emerald-600";
  return "bg-slate-400";
}

function statusLabel(status) {
  if (status === "ingame") return "In Game";
  if (status === "online") return "Online";
  return "Offline";
}

function formatRelativeTime(isoDate) {
  if (!isoDate) return "Agora";
  const ts = Date.parse(isoDate);
  if (Number.isNaN(ts)) return "Agora";
  const secs = Math.max(1, Math.floor((Date.now() - ts) / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

function parseAuctionTime(isoDate) {
  const ts = Date.parse(isoDate || "");
  if (Number.isNaN(ts)) return null;
  return ts;
}

function getAuctionAgeMinutes(isoDate) {
  const ts = parseAuctionTime(isoDate);
  if (!ts) return null;
  return Math.max(0, (Date.now() - ts) / 60000);
}

function getAgeToneClasses(isoDate) {
  const ageMinutes = getAuctionAgeMinutes(isoDate);
  if (ageMinutes == null) return "bg-sky-950/70 border-sky-700";
  if (ageMinutes < 3) return "bg-emerald-950/55 border-emerald-700";
  if (ageMinutes <= 7) return "bg-sky-950/70 border-sky-700";
  return "bg-rose-950/55 border-rose-700";
}

export default function RecentIntroductionsApp() {
  const [auctionRows, setAuctionRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [forbiddenCooldown, setForbiddenCooldown] = useState(null);
  const [forbiddenSecs, setForbiddenSecs] = useState(0);
  const [countdown, setCountdown] = useState(DEFAULT_POLL_INTERVAL / 1000);
  const [copiedAuctionId, setCopiedAuctionId] = useState(null);
  const [weaponOptions, setWeaponOptions] = useState([]);
  const [onlineMinByWeapon, setOnlineMinByWeapon] = useState({});
  const [onlineLowestByWeapon, setOnlineLowestByWeapon] = useState({});
  const [offlineMinByWeapon, setOfflineMinByWeapon] = useState({});
  const [offlineLowestByWeapon, setOfflineLowestByWeapon] = useState({});
  const [checkingWeapons, setCheckingWeapons] = useState(() => new Set());
  const [selectedWeapons, setSelectedWeapons] = useState([]);
  const [alarmMessage, setAlarmMessage] = useState("");
  const [alarmKind, setAlarmKind] = useState("normal");
  const [notificationPermission, setNotificationPermission] = useState(() =>
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );

  const seenIdsRef = useRef(new Set());
  const nextFetchAtRef = useRef(Date.now() + DEFAULT_POLL_INTERVAL);
  const forbiddenUntilRef = useRef(null);
  const firstFetchDoneRef = useRef(false);
  const checkedWeaponsRef = useRef(new Set());
  const weaponCacheRef = useRef(new Set());
  const selectedWeaponsRef = useRef(new Set());
  const alertedVisibleIdsRef = useRef(new Set());
  const visibleAlarmInitializedRef = useRef(false);

  const weaponNameByUrl = useMemo(() => {
    const map = {};
    weaponOptions.forEach((w) => {
      map[w.url_name] = w.item_name;
    });
    return map;
  }, [weaponOptions]);

  const getWeaponDisplayName = useCallback(
    (urlName) => weaponNameByUrl[urlName] || urlName,
    [weaponNameByUrl]
  );

  useEffect(() => {
    selectedWeaponsRef.current = new Set(selectedWeapons);
  }, [selectedWeapons]);

  useEffect(() => {
    const raw = readCookie(SELECTED_WEAPONS_COOKIE);
    if (!raw) return;
    try {
      const parsed = JSON.parse(decodeURIComponent(raw));
      if (!Array.isArray(parsed)) return;
      const sanitized = parsed.filter((item) => typeof item === "string" && item.length > 0);
      if (sanitized.length > 0) setSelectedWeapons(sanitized);
    } catch {
    }
  }, []);

  useEffect(() => {
    const encoded = encodeURIComponent(JSON.stringify(selectedWeapons));
    writeCookie(SELECTED_WEAPONS_COOKIE, encoded, SELECTED_WEAPONS_COOKIE_MAX_AGE);
  }, [selectedWeapons]);

  useEffect(() => {
    if (!forbiddenCooldown) return;
    const until = forbiddenCooldown;
    const update = () => {
      const rem = Math.ceil((until - Date.now()) / 1000);
      if (rem <= 0) {
        setForbiddenSecs(0);
        setForbiddenCooldown(null);
        forbiddenUntilRef.current = null;
        return true;
      }
      setForbiddenSecs(rem);
      return false;
    };
    update();
    const iv = setInterval(() => {
      if (update()) clearInterval(iv);
    }, 500);
    return () => clearInterval(iv);
  }, [forbiddenCooldown]);

  const playAlarm = useCallback((kind = "normal") => {
    if (typeof window === "undefined") return;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    try {
      const ctx = new AudioCtx();
      const sequence =
        kind === "selected"
          ? [
              { f: 200, t: 0.75 },
              { f: 330, t: 0.50 },
              { f: 200, t: 0.75 },
            ]
          : [
              { f: 740, t: 0.75 },
              { f: 880, t: 0.75 },
            ];
      let cursor = ctx.currentTime;
      sequence.forEach((step) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "triangle";
        osc.frequency.value = step.f;
        gain.gain.setValueAtTime(0.001, cursor);
        gain.gain.exponentialRampToValueAtTime(0.7, cursor + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.7, cursor + step.t);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(cursor);
        osc.stop(cursor + step.t);
        cursor += step.t + 0.03;
      });
      setTimeout(() => {
        try {
          ctx.close();
        } catch {
        }
      }, 700);
    } catch {
    }
  }, []);

  const notifyDesktop = useCallback((title, body) => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    try {
      // eslint-disable-next-line no-new
      new Notification(title, { body });
    } catch {
    }
  }, []);

  const requestNotificationPermission = useCallback(async () => {
    if (typeof Notification === "undefined") return;
    try {
      const next = await Notification.requestPermission();
      setNotificationPermission(next);
    } catch {
    }
  }, []);

  const toggleWeaponSelection = useCallback((weaponUrlName) => {
    setSelectedWeapons((prev) => {
      if (prev.includes(weaponUrlName)) {
        return prev.filter((w) => w !== weaponUrlName);
      }
      return [...prev, weaponUrlName];
    });
  }, []);

  const handleWeaponMultiChange = useCallback((values) => {
    const next = Array.isArray(values) ? values.map((item) => item.value) : [];
    setSelectedWeapons(next);
  }, []);

  useEffect(() => {
    fetch("/v1/riven/items")
      .then((r) => r.json())
      .then((data) => {
        const items = data?.payload?.items ?? [];
        setWeaponOptions([...items].sort((a, b) => a.item_name.localeCompare(b.item_name)));
      })
      .catch(() => {});
  }, []);

  const copyTradeMessage = (auctionId, ingameName, weaponName, itemName, price) => {
    if (!ingameName) return;
    navigator.clipboard.writeText(`/w ${ingameName} Hi, WTB ${weaponName} ${itemName} ${price}p. Is it still available? (https://www.rivenappraiser.site/)`);
    setCopiedAuctionId(auctionId);
    setTimeout(() => setCopiedAuctionId((prev) => (prev === auctionId ? null : prev)), 2000);
  };

  const loadOfflineMinimumForWeapon = useCallback(async (weaponUrlName, force = false) => {
    if (!weaponUrlName) return;

    if (!force && weaponCacheRef.current.has(weaponUrlName)) return;
    if (checkedWeaponsRef.current.has(weaponUrlName)) return;
    checkedWeaponsRef.current.add(weaponUrlName);

    setCheckingWeapons((prev) => {
      const next = new Set(prev);
      next.add(weaponUrlName);
      return next;
    });

    try {
      const displayName = getWeaponDisplayName(weaponUrlName);
      const res = await fetch("/.netlify/functions/sniper", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          weaponName: weaponUrlName,
          displayName,
        }),
      });
      if (!res.ok) return;
      const data = await res.json();
      weaponCacheRef.current.add(weaponUrlName);
      setOnlineMinByWeapon((prev) => ({
        ...prev,
        [weaponUrlName]: data?.lowestPrice ?? null,
      }));
      setOnlineLowestByWeapon((prev) => ({
        ...prev,
        [weaponUrlName]: Array.isArray(data?.lowestOnlineAuctions) ? data.lowestOnlineAuctions : [],
      }));
      setOfflineMinByWeapon((prev) => ({
        ...prev,
        [weaponUrlName]: data?.lowestOfflinePrice ?? null,
      }));
      setOfflineLowestByWeapon((prev) => ({
        ...prev,
        [weaponUrlName]: Array.isArray(data?.lowestOfflineAuctions) ? data.lowestOfflineAuctions : [],
      }));
    } catch {
    } finally {
      checkedWeaponsRef.current.delete(weaponUrlName);
      setCheckingWeapons((prev) => {
        const next = new Set(prev);
        next.delete(weaponUrlName);
        return next;
      });
    }
  }, [getWeaponDisplayName]);

  const fetchRecentIntroductions = useCallback(async () => {
    if (forbiddenUntilRef.current && Date.now() < forbiddenUntilRef.current) return;
    setError("");
    try {
      const res = await fetch("/v1/auctions?type=riven");
      if (res.status === 403 || res.status === 429) {
        const until = Date.now() + 60_000;
        forbiddenUntilRef.current = until;
        setForbiddenCooldown(until);
        setForbiddenSecs(60);
        setLoading(false);
        return;
      }
      if (!res.ok) throw new Error(`status ${res.status}`);

      setForbiddenCooldown(null);
      setForbiddenSecs(0);
      forbiddenUntilRef.current = null;

      const data = await res.json();
      const all = (data?.payload?.auctions ?? [])
        .filter((a) => a?.item?.type === "riven")
        .filter((a) => {
          const createdTs = parseAuctionTime(a?.created);
          if (!createdTs) return false;
          return Date.now() - createdTs <= RECENT_WINDOW_MS;
        })
        .slice(0, DEFAULT_RECENT_LIMIT);

      const freshIds = new Set();
      const newWeaponNames = new Set();

      all.forEach((auction) => {
        if (!seenIdsRef.current.has(auction.id)) {
          freshIds.add(auction.id);
          const weaponUrlName = auction?.item?.weapon_url_name;
          if (weaponUrlName) {
            newWeaponNames.add(weaponUrlName);
          }
        }
      });

      all.forEach((auction) => seenIdsRef.current.add(auction.id));

      if (seenIdsRef.current.size > 2000) {
        const keep = all.map((a) => a.id);
        seenIdsRef.current = new Set(keep);
      }

      const mapped = all.map((a) => ({
        id: a.id,
        created: a.created,
        updated: a.updated,
        isDirectSell: a.is_direct_sell,
        buyoutPrice: a.buyout_price,
        startingPrice: a.starting_price,
        owner: {
          ingameName: a.owner?.ingame_name,
          status: a.owner?.status,
        },
        item: {
          name: a.item?.name,
          weaponUrlName: a.item?.weapon_url_name,
          rerolls: a.item?.re_rolls ?? 0,
          modRank: a.item?.mod_rank ?? 0,
        },
        isNew: firstFetchDoneRef.current ? freshIds.has(a.id) : false,
      }));

      setAuctionRows(mapped);
      setLoading(false);

      if (selectedWeaponsRef.current.size === 0) {
        firstFetchDoneRef.current = true;
        return;
      }

      if (newWeaponNames.size > 0 && firstFetchDoneRef.current) {
        newWeaponNames.forEach((weaponUrlName) => {
          loadOfflineMinimumForWeapon(weaponUrlName, true);
        });
      }

      if (!firstFetchDoneRef.current) {
        const initialWeapons = new Set(mapped.map((a) => a.item.weaponUrlName).filter(Boolean));
        initialWeapons.forEach((weaponUrlName) => {
          loadOfflineMinimumForWeapon(weaponUrlName, false);
        });
      }

      firstFetchDoneRef.current = true;
    } catch (err) {
      setLoading(false);
      setError(String(err));
    }
  }, [loadOfflineMinimumForWeapon]);

  useEffect(() => {
    let mounted = true;
    let timerId = null;
    let countdownId = null;

    const tick = async () => {
      if (!mounted) return;
      await fetchRecentIntroductions();
      nextFetchAtRef.current = Date.now() + DEFAULT_POLL_INTERVAL;
      timerId = setTimeout(tick, DEFAULT_POLL_INTERVAL);
    };

    tick();

    countdownId = setInterval(() => {
      if (!mounted) return;
      setCountdown(Math.max(0, Math.ceil((nextFetchAtRef.current - Date.now()) / 1000)));
    }, 500);

    return () => {
      mounted = false;
      clearTimeout(timerId);
      clearInterval(countdownId);
    };
  }, [fetchRecentIntroductions]);

  const visibleAuctions = useMemo(() => {
    return auctionRows.filter((auction) => {
      const weapon = auction.item.weaponUrlName;
      const offlineMin = offlineMinByWeapon[weapon];
      const hasBuyout = typeof auction.buyoutPrice === "number";
      if (!hasBuyout || typeof offlineMin !== "number") return false;

      return auction.buyoutPrice < offlineMin;
    });
  }, [auctionRows, offlineMinByWeapon]);

  useEffect(() => {
    if (loading) return;

    if (!visibleAlarmInitializedRef.current) {
      visibleAuctions.forEach((a) => alertedVisibleIdsRef.current.add(a.id));
      visibleAlarmInitializedRef.current = true;
      return;
    }

    const newlyVisible = visibleAuctions.filter((a) => !alertedVisibleIdsRef.current.has(a.id));
    if (newlyVisible.length === 0) return;

    newlyVisible.forEach((a) => alertedVisibleIdsRef.current.add(a.id));

    if (alertedVisibleIdsRef.current.size > 3000) {
      const keep = visibleAuctions.map((a) => a.id);
      alertedVisibleIdsRef.current = new Set(keep);
    }

    const selectedFresh = newlyVisible.filter((a) => selectedWeaponsRef.current.has(a.item.weaponUrlName));

    if (selectedFresh.length > 0) {
      const first = selectedFresh[0];
      const label = getWeaponDisplayName(first.item.weaponUrlName);
      const msg = `Alerta arma selecionada: ${selectedFresh.length} nova(s). Ex: ${label}`;
      setAlarmKind("selected");
      setAlarmMessage(msg);
      playAlarm("selected");
      notifyDesktop("Nova entrada de arma selecionada", msg);
      return;
    }

    const first = newlyVisible[0];
    const label = getWeaponDisplayName(first.item.weaponUrlName);
    const msg = `Nova entrada na lista: ${newlyVisible.length} (ex: ${label})`;
    setAlarmKind("normal");
    setAlarmMessage(msg);
    playAlarm("normal");
    notifyDesktop("Nova entrada na lista", msg);
  }, [getWeaponDisplayName, loading, notifyDesktop, playAlarm, visibleAuctions]);

  const weaponSelectOptions = useMemo(
    () => weaponOptions.map((w) => ({ value: w.url_name, label: w.item_name })),
    [weaponOptions]
  );

  const selectedWeaponValues = useMemo(() => {
    const selectedSet = new Set(selectedWeapons);
    return weaponSelectOptions.filter((opt) => selectedSet.has(opt.value));
  }, [selectedWeapons, weaponSelectOptions]);

  const weaponMultiSelectStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        minHeight: 38,
        borderRadius: 10,
        borderColor: state.isFocused ? "#0ea5e9" : "#334155",
        backgroundColor: "rgba(15, 23, 42, 0.85)",
        boxShadow: state.isFocused ? "0 0 0 1px #0ea5e9" : "none",
        ":hover": { borderColor: state.isFocused ? "#0ea5e9" : "#475569" },
      }),
      menu: (base) => ({
        ...base,
        zIndex: 40,
        borderRadius: 10,
        border: "1px solid #334155",
        backgroundColor: "#0f172a",
        overflow: "hidden",
      }),
      menuList: (base) => ({
        ...base,
        maxHeight: 220,
      }),
      option: (base, state) => ({
        ...base,
        fontSize: 12,
        backgroundColor: state.isFocused ? "rgba(30, 41, 59, 0.85)" : "transparent",
        color: "#e2e8f0",
        cursor: "pointer",
      }),
      input: (base) => ({ ...base, color: "#e2e8f0", fontSize: 12 }),
      placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 12 }),
      singleValue: (base) => ({ ...base, color: "#e2e8f0" }),
      multiValue: (base) => ({ ...base, backgroundColor: "rgba(14, 165, 233, 0.2)", borderRadius: 9999 }),
      multiValueLabel: (base) => ({ ...base, color: "#bae6fd", fontSize: 11 }),
      multiValueRemove: (base) => ({
        ...base,
        color: "#bae6fd",
        ":hover": { backgroundColor: "rgba(14, 165, 233, 0.28)", color: "#e0f2fe" },
      }),
      indicatorSeparator: (base) => ({ ...base, backgroundColor: "#334155" }),
      dropdownIndicator: (base) => ({ ...base, color: "#94a3b8", ":hover": { color: "#cbd5e1" } }),
      clearIndicator: (base) => ({ ...base, color: "#94a3b8", ":hover": { color: "#cbd5e1" } }),
      noOptionsMessage: (base) => ({ ...base, fontSize: 12, color: "#94a3b8" }),
    }),
    []
  );

  const selectedWeaponOptions = useMemo(() => {
    const map = new Map(weaponOptions.map((w) => [w.url_name, w]));
    return selectedWeapons
      .map((urlName) => map.get(urlName))
      .filter(Boolean);
  }, [selectedWeapons, weaponOptions]);

  return (
    <div className="h-screen w-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-slate-100 overflow-hidden flex flex-col">
      {forbiddenCooldown && (
        <div className="bg-amber-600 text-white text-sm text-center px-4 py-2 font-semibold shadow-sm">
          ⚠️ Pedido bloqueado (403/429) - a retomar em <span className="font-bold">{forbiddenSecs}s</span>...
        </div>
      )}

      <div className="px-4 py-3 border-b border-slate-700 bg-slate-950/80 backdrop-blur-sm flex items-center gap-3">
        <h1 className="text-lg font-bold tracking-tight">Introducoes Recentes</h1>
        <span className="text-xs text-slate-400">Refresh em {countdown}s</span>
        {alarmMessage && (
          <span
            className={`text-xs px-2 py-1 rounded-md border ${
              alarmKind === "selected"
                ? "bg-amber-500/20 text-amber-200 border-amber-400/40"
                : "bg-sky-500/20 text-sky-200 border-sky-400/40"
            }`}
          >
            {alarmMessage}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => {
              nextFetchAtRef.current = Date.now();
              fetchRecentIntroductions();
            }}
            className="text-xs px-3 py-1.5 rounded-lg bg-sky-600 text-white hover:bg-sky-500"
          >
            Atualizar
          </button>
        </div>
      </div>

      <div className="px-4 py-2 text-xs text-slate-300 bg-slate-950/65 border-b border-slate-800 flex items-center gap-4">
        <span>
          Novas entradas destacadas: <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 align-middle" />
        </span>
        <span>
          Alarme especial arma selecionada: <span className="inline-block w-2 h-2 rounded-full bg-amber-400 align-middle" />
        </span>
        <span>
          Tempo: {"<"}3m <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 align-middle" /> | 3-7m <span className="inline-block w-2 h-2 rounded-full bg-sky-500 align-middle" /> | {">"}7m <span className="inline-block w-2 h-2 rounded-full bg-rose-500 align-middle" />
        </span>
      </div>

      <div className="flex-1 p-4 grid grid-cols-1 lg:grid-cols-2 gap-4 overflow-hidden">
        <section className="min-h-0 rounded-xl border border-slate-700/70 bg-slate-950/35 p-3 overflow-y-auto">
          {loading && <p className="text-sm text-slate-400">A carregar leiloes recentes...</p>}
          {error && <p className="text-sm text-rose-400">Erro: {error}</p>}

          {!loading && visibleAuctions.length === 0 && (
            <p className="text-sm text-slate-400">Sem leiloes perto do minimo offline.</p>
          )}

          <ul className="space-y-2">
            {visibleAuctions.map((auction) => {
            const weapon = auction.item.weaponUrlName;
            const onlineMin = onlineMinByWeapon[weapon];
            const onlineLowest = Array.isArray(onlineLowestByWeapon[weapon]) ? onlineLowestByWeapon[weapon] : [];
            const offlineMin = offlineMinByWeapon[weapon];
            const offlineLowest = Array.isArray(offlineLowestByWeapon[weapon]) ? offlineLowestByWeapon[weapon] : [];
            const hasBuyout = typeof auction.buyoutPrice === "number";
            const alertBelowOfflineMin =
              hasBuyout &&
              typeof offlineMin === "number" &&
              auction.buyoutPrice < offlineMin;

            const offlineLowestExcludingCurrent = offlineLowest
              .filter((entry) => entry?.id && entry.id !== auction.id)
              .map((entry) => entry.buyout_price)
              .filter((price) => typeof price === "number")
              .slice(0, LOWEST_VALUES_SAMPLE);

            const onlineLowestExcludingCurrent = onlineLowest
              .filter((entry) => entry?.id && entry.id !== auction.id)
              .map((entry) => entry.buyout_price)
              .filter((price) => typeof price === "number")
              .slice(0, LOWEST_VALUES_SAMPLE);

            const highlightClass = getAgeToneClasses(auction.created);
            const isSelectedWeapon = selectedWeaponsRef.current.has(weapon);

            return (
              <li key={auction.id} className={`rounded-xl p-3 text-sm leading-normal border ${highlightClass}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-slate-100 inline-flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      {auction.buyoutPrice ?? auction.startingPrice ?? "-"}
                      <PlatinumIcon className="w-3.5 h-3.5" />
                    </span>
                    <span className="text-xs font-medium text-slate-400 inline-flex items-center gap-1">
                      Min on (5):
                      {onlineLowestExcludingCurrent.length > 0
                        ? `${onlineLowestExcludingCurrent.join(", ")}p`
                        : typeof onlineMin === "number"
                          ? `${onlineMin}p`
                          : checkingWeapons.has(weapon)
                            ? "a verificar..."
                            : "-"}
                    </span>
                    <span className="text-xs font-medium text-slate-400 inline-flex items-center gap-1">
                      Min off (5):
                      {offlineLowestExcludingCurrent.length > 0
                        ? `${offlineLowestExcludingCurrent.join(", ")}p`
                        : checkingWeapons.has(weapon)
                          ? "a verificar..."
                          : "-"}
                    </span>
                  </span>
                  <span className="flex items-center gap-1">
                    {isSelectedWeapon && (
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 border border-amber-400/35">
                        selecionada
                      </span>
                    )}
                    <span className={`inline-block w-2 h-2 rounded-full ${statusColor(auction.owner.status)}`} />
                    <span className="text-xs text-slate-400 font-medium">{statusLabel(auction.owner.status)}</span>
                  </span>
                </div>

                <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
                  <span className="font-medium text-slate-200">{getWeaponDisplayName(weapon)}</span>
                  <span className="text-slate-600">|</span>
                  <span>{auction.item.name || "riven"}</span>
                  <span className="text-slate-600">|</span>
                  <span>ha {formatRelativeTime(auction.created)}</span>
                </div>

                <div className="flex items-center gap-3 mb-2">
                  <div className="text-xs text-slate-400">Rolls: {auction.item.rerolls}</div>
                  <div className="text-xs text-slate-400">Rank: {auction.item.modRank}</div>
                </div>

                {alertBelowOfflineMin && (
                  <div className="mb-2 inline-flex items-center gap-1 rounded-md bg-sky-950/70 px-2 py-1 text-[11px] font-semibold text-sky-100 border border-sky-800">
                    Alerta: preco abaixo de min offline
                  </div>
                )}

                <div className="flex items-center gap-3 mt-1">
                  <button
                    onClick={() =>
                      copyTradeMessage(
                        auction.id,
                        auction.owner.ingameName,
                        getWeaponDisplayName(weapon),
                        auction.item.name || "riven",
                        auction.buyoutPrice ?? auction.startingPrice ?? ""
                      )
                    }
                    className="text-xs leading-normal text-slate-200 hover:text-sky-300 truncate text-left"
                    title="Copiar mensagem de trade"
                  >
                    {copiedAuctionId === auction.id ? "✓ Copiado!" : auction.owner.ingameName || "-"}
                  </button>

                  <a
                    href={`https://warframe.market/auction/${auction.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-auto inline-block text-xs font-semibold text-sky-300 hover:underline"
                  >
                    Ver leilao ↗
                  </a>
                </div>
              </li>
            );
            })}
          </ul>
        </section>

        <aside className="min-h-0 rounded-xl border border-slate-700/70 bg-slate-950/35 p-3 flex flex-col gap-3 overflow-hidden">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-slate-100">Armas monitorizadas</h2>
            <span className="text-xs text-slate-400">{selectedWeapons.length} selecionada(s)</span>
          </div>

          <div className="flex items-center gap-2">
            <div className="w-full">
              <Select
                isMulti
                isSearchable
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                options={weaponSelectOptions}
                value={selectedWeaponValues}
                onChange={handleWeaponMultiChange}
                styles={weaponMultiSelectStyles}
                placeholder={selectedWeapons.length > 0 ? `${selectedWeapons.length} selected` : "Selecionar armas..."}
                noOptionsMessage={() => "Sem resultados"}
                controlShouldRenderValue={false}
                formatOptionLabel={(option, { context }) => {
                  if (context !== "menu") return option.label;
                  const isSelected = selectedWeapons.includes(option.value);
                  return (
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-flex h-4 w-4 items-center justify-center rounded border text-[10px] ${
                          isSelected
                            ? "border-emerald-500 bg-emerald-500/20 text-emerald-200"
                            : "border-slate-600 text-transparent"
                        }`}
                      >
                        ✓
                      </span>
                      <span className="truncate">{option.label}</span>
                    </div>
                  );
                }}
              />
            </div>
            <button
              onClick={() => setSelectedWeapons([])}
              className="rounded-lg border border-slate-700 px-2.5 py-2 text-xs text-slate-300 hover:text-slate-100 hover:border-slate-500"
            >
              Limpar
            </button>
          </div>

          <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-700/60 bg-slate-900/60 px-3 py-2">
            <span className="text-xs text-slate-300">Notificacoes desktop</span>
            <button
              onClick={requestNotificationPermission}
              disabled={notificationPermission === "granted"}
              className="rounded-md bg-sky-600 px-2.5 py-1 text-xs text-white disabled:bg-slate-700 disabled:text-slate-400"
            >
              {notificationPermission === "granted" ? "Ativas" : "Ativar"}
            </button>
          </div>

          <div className="min-h-0 overflow-y-auto rounded-lg border border-slate-700/60 bg-slate-900/40">
            <div className="px-3 py-2 text-xs font-semibold text-slate-300 border-b border-slate-800/80">
              Selecionadas
            </div>
            {selectedWeaponOptions.length === 0 ? (
              <p className="px-3 py-2 text-xs text-slate-500">Sem armas selecionadas.</p>
            ) : (
              <ul className="divide-y divide-slate-800/80">
                {selectedWeaponOptions.map((weapon) => (
                  <li key={weapon.url_name} className="flex items-center gap-2 px-3 py-2 text-xs text-slate-200">
                    <span className="truncate">{weapon.item_name}</span>
                    <button
                      type="button"
                      onClick={() => toggleWeaponSelection(weapon.url_name)}
                      className="ml-auto rounded-md border border-rose-700/60 bg-rose-900/30 px-2 py-1 text-[11px] text-rose-200 hover:bg-rose-900/50"
                    >
                      Remover
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
